import React, { useState } from 'react';
import { cn } from '../../../utils/cn';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { changePassword } from '../../../lib/profile';

const SecuritySettings = () => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  // 2FA + Active Sessions hidden until backend support exists. Don't render mock data.

  const securityScore = null; // Wire to real audit later

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdatePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if ((passwordData.newPassword || '').length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    const res = await changePassword({
      currentPassword: passwordData.currentPassword,
      newPassword:     passwordData.newPassword,
    });
    if (res.error) { setPasswordError(res.error); return; }
    setPasswordSuccess(true);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setTimeout(() => { setShowPasswordForm(false); setPasswordSuccess(false); }, 1500);
  };

  const getDeviceIcon = (device) => {
    if (device?.toLowerCase()?.includes('iphone') || device?.toLowerCase()?.includes('android')) {
      return 'Smartphone';
    }
    if (device?.toLowerCase()?.includes('ipad') || device?.toLowerCase()?.includes('tablet')) {
      return 'Tablet';
    }
    return 'Monitor';
  };

  const getSecurityScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSecurityScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="max-w-4xl">
      <div className="space-y-6">
        {/* Security Score */}
        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Security Score
              </h2>
              <p className="text-sm text-muted-foreground">
                Overall security status of your account
              </p>
            </div>
            <div className={cn(
              "flex items-center justify-center w-16 h-16 rounded-full",
              getSecurityScoreBg(securityScore)
            )}>
              <span className={cn(
                "text-2xl font-bold",
                getSecurityScoreColor(securityScore)
              )}>
                {securityScore}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-green-50 border border-green-200">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Strong Password</p>
                <p className="text-xs text-green-700">Password meets all requirements</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 rounded-lg bg-green-50 border border-green-200">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Email Verified</p>
                <p className="text-xs text-green-700">Account email is confirmed</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 rounded-lg bg-green-50 border border-green-200">
              <Icon name="Shield" size={20} className="text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Secure Sessions</p>
                <p className="text-xs text-green-700">All sessions are verified</p>
              </div>
            </div>
          </div>
        </div>

        {/* Password Settings */}
        <div className="bg-white rounded-lg border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  Password Security
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage your account password and authentication
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                iconName="Key"
                iconPosition="left"
              >
                Change Password
              </Button>
            </div>
          </div>

          {showPasswordForm && (
            <div className="px-6 py-4 bg-accent/10 border-b border-border">
              <div className="max-w-md space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Current Password
                  </label>
                  <Input
                    type="password"
                    value={passwordData?.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
                    placeholder="Enter current password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordData?.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordData?.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
                    placeholder="Confirm new password"
                  />
                </div>

                {passwordError && (
                  <p className="text-sm text-destructive">{passwordError}</p>
                )}
                {passwordSuccess && (
                  <p className="text-sm text-green-600">Password updated.</p>
                )}
                <div className="flex items-center space-x-3 pt-4">
                  <Button
                    onClick={handleUpdatePassword}
                    iconName="Check"
                    iconPosition="left"
                    size="sm"
                  >
                    Update Password
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowPasswordForm(false)}
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>


      </div>
    </div>
  );
};

export default SecuritySettings;